import { FC, useEffect, useState } from 'react';
import { Flex, Avatar, Typography, Upload, Spin } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useFileUpload, usePermission } from 'hooks';
import { PERMISSION_ROUTES, PERMISSION_ACTIONS } from 'constants/permissions';
import { updateDoctor } from 'services/api/doctors';

import { useDoctorProfile } from 'pages/DoctorProfile/DoctorProfileContext';
import { StyledDoctor } from './styled';
import { getFileUrl } from 'utils/file';
import { pickLocale } from 'utils/pickLocale';
import { Private } from 'components/shared';
import { showErrorMessage } from 'utils/showErrorMessage';
import useDoctorProfileCache from 'hooks/useDoctorProfileCache';

interface IProps {
  id: string;
  lang: string;
  allowToEdit?: boolean;
}

const DoctorInfo: FC<IProps> = ({ id, lang, allowToEdit }) => {
  const { checkUserPermission } = usePermission();
  const { profile, validateProfile } = useDoctorProfileCache({ id, lang });
  const { refresh } = useDoctorProfile();
  const [avatar, setAvatar] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const { upload, loading } = useFileUpload({
    loading: true
  });

  useEffect(() => {
    if (!avatar || avatar === profile?.avatar) return;
    const doctorId = id || profile?.id;
    if (!doctorId) return;

    let cancelled = false;

    const save = async () => {
      try {
        setIsUpdating(true);
        await updateDoctor(doctorId, { avatar, lang });
        if (cancelled) return;
        setAvatar('');
        refresh();
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setIsUpdating(false);
      }
    };

    save();

    return () => {
      cancelled = true;
    };
  }, [avatar, id, lang, profile?.avatar, profile?.id, refresh]);

  const hasProfileUpdatePermission = checkUserPermission(
    PERMISSION_ROUTES.doctors,
    PERMISSION_ACTIONS.update
  );

  return (
    <StyledDoctor>
      <Flex align="center" gap={18}>
        <Upload
          accept="image/*"
          disabled={
            !hasProfileUpdatePermission || loading || isUpdating || !allowToEdit
          }
          customRequest={e =>
            validateProfile(() =>
              upload(e.file as File).then(val => setAvatar(val || ''))
            )
          }
          showUploadList={false}
          className="avatar-container"
        >
          {loading ? (
            <Flex
              style={{ width: 60, height: 60 }}
              justify="center"
              align="center"
            >
              <Spin />
            </Flex>
          ) : (
            <>
              <Avatar src={getFileUrl(avatar || profile?.avatar)} size={60} />
              <Private
                route={PERMISSION_ROUTES.doctors}
                action={PERMISSION_ACTIONS.update}
              >
                <span className="avatar-edit-icon">
                  <EditOutlined />
                </span>
              </Private>
            </>
          )}
        </Upload>
        <div>
          <Typography.Title level={5}>
            {`${pickLocale(profile?.first_name, lang)} ${pickLocale(
              profile?.last_name,
              lang
            )}`.trim()}
          </Typography.Title>
          <a className="doctor-email" href={`mailto:${profile?.email}`}>
            {profile?.email}
          </a>
        </div>
      </Flex>
    </StyledDoctor>
  );
};

export default DoctorInfo;
